/* eslint-disable no-console */
/* eslint-disable no-undef */
/**
 *
 * Store locator helper variables and functions
 * --------------------------------------------
 *
 */

const sandboxEndpoint = `https://sandbox.uberall.com/api/store-finders/${cnvs.storeLocator.sandboxStoreKey}/locations/`
const productionEndpoint = `https://uberall.com/api/store-finders/${cnvs.storeLocator.productionStoreKey}/locations/`

export const storeLocatorProps = {
  activeEnvironmentUrl: window.cnvs.storeLocator.prodEnvOn ? productionEndpoint : sandboxEndpoint,
  landingPageStoreFields: '?fieldMask=city&' +
    'fieldMask=country&' +
    'fieldMask=id&' +
    'fieldMask=lat&' +
    'fieldMask=lng&' +
    'fieldMask=name&' +
    'fieldMask=nextOpen&' +
    'fieldMask=openingHours&' +
    'fieldMask=openNow&' +
    'fieldMask=phone&' +
    'fieldMask=province&' +
    'fieldMask=streetAndNumber&' +
    'fieldMask=shopify_store_page_url&' +
    'fieldMask=futureOpeningDate&' +
    'fieldMask=zip&',
  landingPageServiceField: '?fieldMask=services&fieldMask=id',
  storePageFields: '?fieldMask=city&' +
    'fieldMask=country&' +
    'fieldMask=id&' +
    'fieldMask=lat&' +
    'fieldMask=lng&' +
    'fieldMask=name&' +
    'fieldMask=nextOpen&' +
    'fieldMask=openingHours&' +
    'fieldMask=openNow&' +
    'fieldMask=phone&' +
    'fieldMask=email&' +
    'fieldMask=photos&' +
    'fieldMask=province&' +
    'fieldMask=streetAndNumber&' +
    'fieldMask=zip&' +
    'fieldMask=services&' +
    'fieldMask=events_custom',
  storePageBannerField: '?fieldMask=Secondary_image',
  storePageBlogFields: '?fieldMask=Blog_one&fieldMask=Blog_two&fieldMask=Blog_one_cta_copy&fieldMask=Blog_two_cta_copy',
  storePageAboutField: '?fieldMask=About_store',
  shopifyPageHandle: '?fieldMask=shopify_store_page_url',
}

let geocoder = null
let isMobile = null

/**
 * Refine store name and remove prefix
 * @param {String} storeName
 * @returns
 */
export function refineStoreName(storeName) {
  return storeName.split('Veronica Beard')?.pop()?.trim()
}

/**
 * Get Store open indicator string
 * Show "Temporarily Closed" for Palisades Village
 * @param {Object} storeData
 * @returns
 */
export function getStoreHours(storeData) {
  if (storeData.futureOpeningDate) {
    const curDate = new Date()
    const futureOpeningDate = new Date(storeData.futureOpeningDate)

    if (futureOpeningDate > curDate) {
      return getFutureOpeningMessage(storeData)
    }
  }

  if (storeData.nextOpen?.neverOpens) {
    return cnvs.strings.store_locator.temporarily_closed
  }

  if (!storeData.openNow) {
    return opensAtHours(storeData)
  }

  return currentlyOpenHours(storeData)
}

/**
 * Calculate flat distance between store and user location (for sorting)
 * @param userCoordinates
 * @param locationCoordinates
 */
export function calculateFlatDistance(userCoordinates, locationCoordinates) {
  const latDiff = locationCoordinates.lat - userCoordinates.lat
  const lngDiff = locationCoordinates.lng - userCoordinates.lng
  const milesPerDegree = 69
  const latDistanceMiles = latDiff * milesPerDegree
  const lngDistanceMiles =
    lngDiff *
    milesPerDegree *
    Math.cos((userCoordinates.lat + locationCoordinates.lat) / 2 * (Math.PI / 180))

  const distanceInMiles =
    Math.sqrt((latDistanceMiles * latDistanceMiles) + (lngDistanceMiles * lngDistanceMiles))

  return Math.round(distanceInMiles * 10) / 10
}

/**
 * Get user geolocation
 */
export function getUserGeolocation() {
  return new Promise((resolve, reject) => {
    if ('geolocation' in navigator) {
      navigator.geolocation.watchPosition((position) => {
        const lat = position.coords.latitude
        const lng = position.coords.longitude

        resolve({ lat, lng })
      }, (error) => {
        reject(error)
      })
    } else {
      reject(new Error('Geolocation is not supported'))
    }
  })
}

/**
 * Get store open hours per days of the week
 * @param {Object} storeData
 * @returns
 */
export function getOpenHours(storeData) {
  const hoursArray = storeData?.openingHours
  const dayWeekHourFrom = hoursArray[0]?.from1?.split(':')?.[0]
  const dayWeekHourTo = hoursArray[0]?.to1?.split(':')?.[0]
  const dayWeekHourToRefined =
    Number(dayWeekHourTo) > 12 ? Number(dayWeekHourTo) - 12 : dayWeekHourTo
  const weekendHourFrom = hoursArray[6]?.from1?.split(':')?.[0]
  const weekendHourTo = hoursArray[6]?.to1?.split(':')?.[0]
  const weekendHourToRefined =
    Number(weekendHourTo) > 12 ? Number(weekendHourTo) - 12 : weekendHourTo

  return [
    {
      days: `${cnvs.strings.store_locator.monday} - ${cnvs.strings.store_locator.saturday}`,
      hour: `${dayWeekHourFrom}am-${dayWeekHourToRefined}pm`,
    },
    {
      days: 'Sunday',
      hour: `${weekendHourFrom}am-${weekendHourToRefined}pm`,
    },
  ]
}

/**
 * Initialize Geocoder
 * @returns
 */
export async function initGeocoder() {
  if (!google) {
    return null
  }

  const { Geocoder } = await google.maps.importLibrary('geocoding')

  geocoder = new Geocoder()

  return geocoder
}

/**
 * Set device type
 * @param {Boolean} isDeviceMobile
 */
export function setDeviceMobile(isDeviceMobile) {
  isMobile = isDeviceMobile
}

/**
 * Build Coming soon message
 * @param {Object} storeData
 * @returns Coming soon date
 */
function getFutureOpeningMessage(storeData) {
  const dateSplitted = storeData.futureOpeningDate.split('-')
  const day = Number(dateSplitted[2]) || 1
  const month = Number(dateSplitted[1]) || 1
  const year = Number(dateSplitted[0]) % 2000 || 25
  const newDateString = `${month}-${day}-${year}`

  return cnvs.strings.store_locator.coming_soon.replace('{{#}}', newDateString)
}

/**
 * Set hours when store opens
 */
function opensAtHours(storeData) {
  const openHour = storeData.nextOpen?.hour?.split(':')?.[0]

  if (!openHour) {
    return ''
  }

  return cnvs.strings.store_locator.opens_at.replace('{{#}}', openHour)
}

/**
 * Set open until time
 */
function currentlyOpenHours(storeData) {
  const openUntil = openUntilHours(storeData.openingHours)
  const openUntilRefined = openUntil?.split(':')?.[0]

  if (!openUntil || !openUntilRefined) {
    return ''
  }

  return cnvs.strings.store_locator.open_until.replace('{{#}}', openUntilRefined - 12)
}

/**
 * Get today store open until
 */
function openUntilHours(hours) {
  const currentDate = new Date()
  const dayOfWeek = currentDate.getDay()

  return hours[dayOfWeek - 1]?.to1
}

/**
 * Build url to show store on map
 * @param {Object} coordinates
 * @param {String} name
 * @returns
 */
export function openStoreOnMap(coordinates, name) {
  if (!geocoder) {
    return
  }

  geocoder.geocode({ location: coordinates }, (results, status) => {
    if (status === google.maps.GeocoderStatus.OK) {
      if (results) {
        const vbStore = results.find((result) => {
          return result.types?.includes('store')
        })
        const destination = `destination=${name.toLowerCase().replace(/ /g, '-')}`
        const destinationId = `destination_place_id=${vbStore?.place_id}`

        openMap(`https://www.google.com/maps/dir/?api=1&${destination}&${destinationId}`)
        return
      }

      console.error('No results found.')
      return
    }

    console.error(`Geocoder failed due to: ${status}`)
  })
}

/**
 * Open Map application if available on Mobile
 * Otherwise open map in browser
 * @param {String} googleMapsURL
 */
function openMap(googleMapsURL) {
  if (isMobile) {
    window.location.href = googleMapsURL
  } else {
    window.open(googleMapsURL, '_blank')
  }
}
